import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider, Helmet } from "react-helmet-async";
import Lenis from "lenis";
import "@/App.css";
import { Nav } from "@/components/site/Nav";
import { Footer } from "@/components/site/Footer";
import { MobileCTA } from "@/components/site/MobileCTA";
import { CookieConsent } from "@/components/site/CookieConsent";
import { Toaster } from "@/components/ui/sonner";
import { useSettings } from "@/lib/api";
import Home from "@/pages/HomePage";
import ServicePage from "@/pages/ServicePage";
import InfoPage from "@/pages/InfoPage";
import { INFO_PAGES } from "@/data/infoPages";
import LandingPage from "@/pages/LandingPage";
import { LANDING_PAGES, LANDING_REDIRECTS } from "@/data/landing";
import { Results, CaseStudy } from "@/pages/Results";
import CaseStudyPage from "@/pages/CaseStudyPage";
import Pricing from "@/pages/Pricing";
import About from "@/pages/About";
import { Blog, BlogPost } from "@/pages/Blog";
import Contact from "@/pages/Contact";
import Legal from "@/pages/Legal";
import Admin from "@/pages/Admin";
import NotFound from "@/pages/NotFound";

const SERVICE_SLUGS = ["google-ads-management", "facebook-instagram-ads", "seo", "google-business-profile", "website-design-development", "analytics-tracking-cro", "digital-marketing-audits"];

let lenis = null;

const SmoothScroll = () => {
  const { pathname, search, hash } = useLocation();
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    let id;
    const raf = (t) => { lenis.raf(t); id = requestAnimationFrame(raf); };
    id = requestAnimationFrame(raf);
    return () => { cancelAnimationFrame(id); lenis.destroy(); lenis = null; };
  }, []);
  useEffect(() => {
    if (hash) {
      const t = setTimeout(() => {
        const el = document.querySelector(hash);
        if (el) (lenis ? lenis.scrollTo(el, { offset: -80 }) : el.scrollIntoView());
      }, 350);
      return () => clearTimeout(t);
    }
    if (lenis) lenis.scrollTo(0, { immediate: true }); else window.scrollTo(0, 0);
    return undefined;
  }, [pathname, search, hash]);
  return null;
};

const SiteMeta = () => {
  const { gsc_verification } = useSettings();
  return gsc_verification ? <Helmet><meta name="google-site-verification" content={gsc_verification} /></Helmet> : null;
};

const Shell = () => {
  const { pathname } = useLocation();
  const admin = pathname.startsWith("/admin");
  return (
    <>
      <SmoothScroll />
      <SiteMeta />
      {!admin && <Nav />}
      <main id="main">
        <Routes>
          <Route path="/" element={<Home />} />
          {SERVICE_SLUGS.map((s) => <Route key={s} path={`/${s}`} element={<ServicePage slugOverride={s} />} />)}
          {Object.keys(INFO_PAGES).map((s) => <Route key={s} path={`/${s}`} element={<InfoPage slug={s} />} />)}
          {Object.keys(LANDING_PAGES).map((s) => <Route key={s} path={`/${s}`} element={<LandingPage key={s} slug={s} />} />)}
          {Object.entries(LANDING_REDIRECTS).map(([from, to]) => <Route key={from} path={from} element={<Navigate to={to} replace />} />)}
          <Route path="/results" element={<Results />} />
          <Route path="/results/halo-gym" element={<Navigate to="/case-studies/halo-gym-seo" replace />} />
          <Route path="/case-studies/halo-gym" element={<Navigate to="/case-studies/halo-gym-seo" replace />} />
          <Route path="/results/:slug" element={<CaseStudy />} />
          <Route path="/case-studies" element={<Navigate to="/results" replace />} />
          <Route path="/case-studies/:slug" element={<CaseStudyPage />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/about-freya-jones" element={<About />} />
          <Route path="/about-freya" element={<Navigate to="/about-freya-jones" replace />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/privacy-policy" element={<Legal kind="privacy" />} />
          <Route path="/cookie-policy" element={<Legal kind="cookie" />} />
          <Route path="/admin/*" element={<Admin />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
      {!admin && <Footer />}
      {!admin && <MobileCTA />}
      {!admin && <CookieConsent />}
      <Toaster richColors position="top-center" />
    </>
  );
};

export default function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <Shell />
      </BrowserRouter>
    </HelmetProvider>
  );
}
